<script setup lang="ts" generic="T">
import { QIcon, QItem, QSelect, QVirtualScroll } from 'quasar'
import { computed, onMounted, ref, useSlots } from 'vue'
import { Color } from '@previo/codebooks'
import type { PValidationRule } from '../../types/types'
import { useHandleScrollQSelect } from '../../composables/menu/handleScrollQSelect'
import { useMenuMaxHeight } from '../../composables/menu/useMenuMaxHeight'
import PTextFormBody from '../typography/form/PTextFormBody.vue'
import { usePrevioDS } from '../../composables/helpers'
import PContextMenu from '../context-menu/PContextMenu.vue'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import PTextDescription from '../typography/system/PTextDescription.vue'
import { PCardSize } from '../card/types'
import PCard from '../card/PCard.vue'
import PDivider from '../divider/PDivider.vue'
import { PMaxLines, PWhiteSpace } from '../typography/types'
import { PInputAutofocusType, type PSelectGroup } from './types'
import { menuOffset } from './composable/menuOffset'
import InputFieldLabel from './components/LabelInputField.vue'

type GroupHeader = { __isGroupHeader: true; key: string; label: string; __isFirstGroup: boolean }
type GroupedOption<T> = {
  __isGroupOption: true
  __value: string | number
  __label: string
  __groupKey: string
  __original: T
  __isFirstInGroup: boolean
  __isLastInGroup: boolean
  __isLastGroup: boolean
}

type Props = {
  label?: null | string
  placeholder?: null | string
  hint?: null | string
  rules?: PValidationRule[]
  tooltip?: null | string
  autofocus?: boolean
  autofocusType?: null | PInputAutofocusType
  errorMessage?: null | string
  items: T[] | []
  itemValue: keyof T
  itemText: ((item: T) => string) | keyof T
  itemDisabled?: ((item: T) => boolean) | keyof T
  noData?: null | string
  disable?: boolean
  readonly?: boolean
  debounce?: null | number
  disableClearable?: boolean
  groups?: PSelectGroup<T>[]
  cy: string
}

const model = defineModel<number | string | null>({ default: null })
const groupModel = defineModel<string | null>('group', { default: null })

const props = withDefaults(defineProps<Props>(), {
  label: null,
  tooltip: null,
  autofocus: false,
  autofocusType: PInputAutofocusType.INPUT,
  disable: false,
  readonly: false,
  debounce: 0,
  disableClearable: false,
  rules() {
    return []
  },
})

defineEmits(['blur'])

const qSelectComponent = ref<InstanceType<typeof QSelect>>()
const handleScrollQSelect = useHandleScrollQSelect(qSelectComponent)
const menuMaxHeight = useMenuMaxHeight(() => qSelectComponent.value?.$el as HTMLElement | undefined)

const popupContentClass = computed<string>(() => {
  return ['p-pa-sm', menuMaxHeight.popupContentClass.value].filter(Boolean).join(' ')
})

const slots = useSlots()
const ds = usePrevioDS()
const isMobileDialogShow = ref<boolean>(false)

const isGroupHeader = (item: T | GroupHeader | GroupedOption<T>): item is GroupHeader => {
  return typeof item === 'object' && item !== null && '__isGroupHeader' in item
}

const isGroupedOption = (item: T | GroupHeader | GroupedOption<T>): item is GroupedOption<T> => {
  return typeof item === 'object' && item !== null && '__isGroupOption' in item
}

const groupItemLabel = (item: T, group: PSelectGroup<T>): string => {
  return typeof group.itemText === 'function'
    ? group.itemText(item)
    : (item[group.itemText] as string)
}

const flatOptions = computed<(T | GroupHeader | GroupedOption<T>)[]>(() => {
  if (!props.groups) {
    return props.items as T[]
  }

  const result: (GroupHeader | GroupedOption<T>)[] = []
  const lastGroupIndex = props.groups.length - 1

  for (const [groupIndex, group] of props.groups.entries()) {
    result.push({
      __isGroupHeader: true,
      key: group.key,
      label: group.label,
      __isFirstGroup: groupIndex === 0,
    })

    const lastIndex = group.items.length - 1

    for (const [index, item] of group.items.entries()) {
      result.push({
        __isGroupOption: true,
        __value: item[group.itemValue] as string | number,
        __label: groupItemLabel(item, group),
        __groupKey: group.key,
        __original: item,
        __isFirstInGroup: index === 0,
        __isLastInGroup: index === lastIndex,
        __isLastGroup: groupIndex === lastGroupIndex,
      })
    }
  }

  return result
})

const isRequired = computed<boolean>(() => {
  return props.rules.includes(ds.rules.required)
})

const isEnableClearable = computed<boolean>(() => {
  if (props.disableClearable || props.readonly || isRequired.value) {
    return false
  }

  return model.value !== null
})

const hasAnyErrors = computed<boolean>(() => {
  return !!props.errorMessage
})

const selectedItem = computed<null | T>(() => {
  if (!props.groups) {
    return (props.items as T[]).find((i: T) => i[props.itemValue] === model.value) ?? null
  }

  for (const group of props.groups) {
    const found = group.items.find((i) => i[group.itemValue] === model.value)

    if (found) {
      return found
    }
  }

  return null
})

const selectedGroupConfig = computed<PSelectGroup<T> | null>(() => {
  if (!props.groups || model.value === null) {
    return null
  }

  return props.groups.find((g) => g.items.some((i) => i[g.itemValue] === model.value)) ?? null
})

const selectedItemLabel = computed<string | null>(() => {
  if (!selectedItem.value) {
    return null
  }

  if (selectedGroupConfig.value) {
    return groupItemLabel(selectedItem.value, selectedGroupConfig.value)
  }

  return itemLabel(selectedItem.value)
})

const isSelected = (id: string): boolean => {
  return model.value === id
}

const updateGroupModel = (value: string | number): void => {
  if (!props.groups) {
    return
  }
  const matchedGroup = props.groups.find((g) => g.items.some((i) => i[g.itemValue] === value))
  groupModel.value = matchedGroup?.key ?? null
}

const onSelect = (value: number | string | null): void => {
  model.value = value
  if (value !== null) {
    updateGroupModel(value)
  } else {
    groupModel.value = null
  }
}

const selectItem = (item: T): void => {
  const value = item[props.itemValue] as string | number
  model.value = value
  updateGroupModel(value)
  isMobileDialogShow.value = false
}

const selectGroupedItem = (opt: GroupedOption<T>): void => {
  model.value = opt.__value
  groupModel.value = opt.__groupKey
  isMobileDialogShow.value = false
}

const clear = (): void => {
  model.value = null
  groupModel.value = null

  // After click in clear icon we don`t want to show popup (Quasar show it automatically)
  setTimeout(() => {
    qSelectComponent.value?.hidePopup()
  }, 1)
}

const itemLabel = (item: T): string => {
  return typeof props.itemText === 'function'
    ? props.itemText(item)
    : (item[props.itemText] as string)
}

const optionLabel = computed<string | ((item: T) => string)>(() => {
  if (props.groups) {
    return '__label'
  }

  return typeof props.itemText === 'function' ? props.itemText : (props.itemText as string)
})

const onMenuShow = (): void => {
  if (ds.screen().isMobile) {
    qSelectComponent.value?.hidePopup()
    isMobileDialogShow.value = true

    return
  }

  handleScrollQSelect.subscribe()
  menuMaxHeight.subscribe()
}

const isItemDisabled = (item: T): boolean => {
  if (!props.itemDisabled) {
    return false
  }

  return typeof props.itemDisabled === 'function'
    ? props.itemDisabled(item)
    : Boolean(item[props.itemDisabled])
}

const onMenuHide = (): void => {
  if (ds.screen().isMobile) {
    return
  }

  handleScrollQSelect.unsubscribe()
  menuMaxHeight.unsubscribe()
}

const showPopup = (): void => {
  if (props.disable || props.readonly) {
    return
  }

  if (ds.screen().isMobile) {
    isMobileDialogShow.value = true

    return
  }

  qSelectComponent.value?.showPopup()
}

onMounted(() => {
  if (props.autofocus && props.autofocusType === PInputAutofocusType.DROPDOWN) {
    showPopup()
  }
})
</script>

<template>
  <input-field-label :label="label" :is-required="isRequired" :tooltip="tooltip" />

  <q-select
    ref="qSelectComponent"
    popup-no-route-dismiss
    :model-value="model"
    :rules="rules"
    lazy-rules
    :input-debounce="debounce ?? 0"
    :placeholder="placeholder"
    :hint="hint ?? undefined"
    :options="flatOptions"
    :option-value="groups ? '__value' : (itemValue as string)"
    :option-label="optionLabel"
    :option-disable="
      (opt: T | GroupHeader | GroupedOption<T>) =>
        isGroupHeader(opt) || (!groups && isItemDisabled(opt as T))
    "
    :error="hasAnyErrors"
    :error-message="errorMessage ?? undefined"
    :autofocus="autofocus"
    autocomplete="nope"
    :disable="disable"
    :readonly="readonly"
    :class="{ 'ds-input-field--readonly': readonly }"
    :bg-color="Color.WHITE"
    dropdown-icon="fa-light fa-chevron-down"
    clear-icon="fa-light fa-xmark"
    behavior="menu"
    :menu-offset="menuOffset"
    :popup-content-class="popupContentClass"
    :popup-content-style="menuMaxHeight.popupContentStyle.value"
    map-options
    outlined
    no-error-icon
    hide-bottom-space
    emit-value
    :data-cy="cy"
    class="p-select-input-field"
    @blur="$emit('blur')"
    @popup-show="onMenuShow"
    @popup-hide="onMenuHide"
    @update:model-value="onSelect"
  >
    <template v-if="slots['options-header']" #before-options>
      <slot name="options-header" />
    </template>

    <template v-if="selectedItem" #selected>
      <slot v-if="!!slots.selected" name="selected" :item="selectedItem" />
      <p-text-form-body
        v-else
        :text="selectedItemLabel ?? ''"
        :white-space="PWhiteSpace.NOWRAP"
        ellipsis
      />
    </template>

    <template v-if="model == null" #prepend>
      <p-text-form-body :text="placeholder" :color="Color.GREY_400" :max-lines="PMaxLines.ONE" />
    </template>

    <template
      #option="{
        itemProps,
        opt,
        selected,
        index,
      }: {
        itemProps: object
        opt: T | GroupHeader | GroupedOption<T>
        selected: boolean
        index: number
      }"
    >
      <template v-if="!!groups">
        <!-- One root element per virtual-scroll item: QSelect's virtual scroll maps each direct
             child of the options content to a single index, so each option must render exactly
             one wrapper element (otherwise sibling dividers/spaces corrupt the size table and
             break scroll position / keyboard scrollTo). -->
        <div v-if="isGroupHeader(opt)" :key="`header-${opt.key}`">
          <p-space v-if="!opt.__isFirstGroup" :mobile="PSpaceSize.LG" vertical />

          <slot :name="`group-${opt.key}-label`">
            <p-text-description :text="opt.label" :color="Color.GREY_500" />
          </slot>

          <p-space :mobile="PSpaceSize.SM" vertical />
        </div>

        <div v-if="isGroupedOption(opt)" :key="opt.__value">
          <q-item v-bind="itemProps" :data-cy="`item-${opt.__value}`">
            <template v-if="!!slots['item']">
              <slot name="item" :item="opt.__original" :is-selected="selected" />
            </template>

            <p-text-form-body
              v-else
              :text="opt.__label"
              :color="selected ? Color.PRIMARY : Color.GREY_600"
            />
          </q-item>

          <p-divider v-if="!opt.__isLastInGroup" />
        </div>
      </template>

      <div v-else :key="String((opt as T)[props.itemValue])">
        <q-item
          v-bind="itemProps"
          :disable="isItemDisabled(opt as T)"
          :data-cy="`item-${(opt as T)[props.itemValue]}`"
          class="p-items-center"
        >
          <template v-if="!!slots['item']">
            <slot name="item" :item="opt as T" :is-selected="selected" />
          </template>

          <p-text-form-body
            v-else
            :text="itemLabel(opt as T)"
            :color="selected ? Color.PRIMARY : Color.GREY_600"
          />
        </q-item>

        <p-divider v-if="index < flatOptions.length - 1" />
      </div>
    </template>

    <template v-if="isEnableClearable" #append>
      <q-icon :color="Color.GREY_400" name="fa-light fa-xmark" @click="clear()" />
    </template>

    <template #no-option>
      <q-item>
        <div class="p-flex p-flex-center">
          <p-text-form-body :text="noData" />
        </div>
      </q-item>
    </template>
  </q-select>

  <p-context-menu
    v-model="isMobileDialogShow"
    :title="`Select ${label?.toLowerCase() ?? ``}`"
    cy="mobile-select"
  >
    <slot name="options-header" />

    <template v-if="groups">
      <div
        v-for="opt in flatOptions"
        :key="
          isGroupHeader(opt)
            ? `header-${opt.key}`
            : isGroupedOption(opt)
              ? opt.__value
              : String((opt as T)[itemValue as keyof T])
        "
      >
        <div v-if="isGroupHeader(opt)">
          <p-space v-if="!opt.__isFirstGroup" :mobile="PSpaceSize.LG" vertical />

          <slot :name="`group-${opt.key}-label`">
            <p-text-description :text="opt.label" :color="Color.GREY_500" />
          </slot>

          <p-space :mobile="PSpaceSize.SM" vertical />
        </div>

        <template v-if="isGroupedOption(opt)">
          <q-item clickable style="padding: 0; min-height: auto" @click="selectGroupedItem(opt)">
            <p-card :pa-mobile="PCardSize.MD" class="p-flex-1">
              <template v-if="!!slots['item']">
                <slot name="item" :item="opt.__original" :is-selected="model === opt.__value" />
              </template>

              <p-text-form-body
                v-else
                :text="opt.__label"
                :color="model === opt.__value ? Color.PRIMARY : Color.GREY_600"
              />
            </p-card>
          </q-item>

          <p-divider />
          <p-space
            v-if="
              (opt as GroupedOption<T>).__isLastInGroup && !(opt as GroupedOption<T>).__isLastGroup
            "
            :mobile="PSpaceSize.LG"
            vertical
          />
        </template>
      </div>
    </template>

    <q-virtual-scroll v-else :items="flatOptions" separator class="p-flex-1">
      <template #default="{ item }">
        <q-item
          :clickable="!isItemDisabled(item)"
          :disable="isItemDisabled(item)"
          @click="selectItem(item)"
        >
          <div class="p-flex p-flex-center p-no-wrap">
            <template v-if="!!slots['item']">
              <slot name="item" :item="item" :is-selected="isSelected(item[itemValue])" />
            </template>

            <p-text-form-body
              v-else
              :text="itemLabel(item)"
              :color="isSelected(item[itemValue]) ? Color.PRIMARY : Color.GREY_600"
            />
          </div>
        </q-item>
      </template>
    </q-virtual-scroll>
  </p-context-menu>
</template>
